import { GALLERY_TAKES, HERO_TAKE } from '../../domain/cars/carSchema';

/**
 * Where a car's pictures live, and which rungs of each one exist.
 *
 * THIS IS A SEAM, and it is the only one in the slice. The day
 * `domain/cars/carMedia.js` exists this file becomes an import of it and nothing
 * above changes.
 *
 * The answer comes out of the RECORD rather than out of a convention written
 * here, and both halves of that are load-bearing:
 *
 *   - `media.base` is where the art build actually wrote the files
 *     (`/assets/content/cars/<id>`, beside the JSON that names them, because
 *     they are one tree and one copy pattern ships them). A second spelling of
 *     that path in the app is a directory that only exists in this file, and
 *     every `<img>` in the product 404s while the markup still looks right.
 *   - `media.widths` is MEASURED per take by `scripts/lib/artTiers.js`, never
 *     assumed, because within one car two takes have different ladders: the
 *     F40's side profile is delivered wide enough for 1600 and its gallery takes
 *     stop at 960. A `srcset` that advertises a candidate nobody wrote is a 404
 *     the browser only asks for on the widest screens — the one place it is
 *     least likely to be noticed.
 *
 * The constants below are the fallback for a record that predates either field.
 * They are the build's own convention, not a second opinion about it.
 *
 * WebP throughout. These are cut-outs on transparency over a tinted plate, so
 * the alpha channel is not optional, and WebP is the only widely-served format
 * that carries alpha at a size a phone should be asked to download.
 */

export const ART_DIR = '/assets/content/cars';

/** `artTiers.ART_TIERS`, restated only as the fallback described above. */
export const ART_WIDTHS = [480, 960, 1600];

/**
 * Which takes this car actually has.
 *
 * Read the same way `carSchema.catalogueTake` reads it, deliberately: two
 * spellings of "where the takes live" is how a card ends up drawing a picture
 * the schema has already decided is not there.
 */
export function takesOf(car) {
    return car?.media?.takes || car?.art?.takes || [];
}

function artBase(car) {
    return car?.media?.base || `${ART_DIR}/${car?.id}`;
}

/** The rungs the build wrote for THIS take, which is not the same for every one. */
function widthsOf(car, take) {
    const measured = car?.media?.widths?.[take];

    return Array.isArray(measured) && measured.length > 0 ? measured : ART_WIDTHS;
}

/**
 * The `src` and `srcSet` for one take, or NULL where the car has no such take.
 *
 * Null rather than a composed path, because most of the twelve have only a side
 * profile and a front view, and the gallery has to degrade to nothing for them
 * rather than to nine broken images. Every caller either has a take it knows
 * exists (the hero, via `carSchema.heroTake`) or is a band that renders nothing
 * when this is null (the gallery).
 */
export function artSource(car, take = HERO_TAKE) {
    if (!takesOf(car).includes(take)) {
        return null;
    }

    const stem = `${artBase(car)}/${take}`;
    const widths = widthsOf(car, take);

    return {
        take,
        // The smallest rung as the fallback. A browser that ignores `srcset` —
        // and the one that matters is an old in-app webview, which is exactly
        // where a shared link lands — gets the cheapest file rather than the
        // largest. It will look soft; it will not cost a megabyte.
        src: `${stem}-${widths[0]}.webp`,
        srcSet: widths.map((width) => `${stem}-${width}.webp ${width}w`).join(', '),
    };
}

/**
 * Every take this car has THAT A READER WALKS, in the schema's own order.
 *
 * `GALLERY_TAKES` rather than the record's own list, and the difference is the
 * whole point of the two lists existing. A record now carries takes that are not
 * pictures to browse: `SELECT_TAKE` is the window-down shot, drawn at the moment
 * a car is chosen and nowhere else. Walking `takesOf` here would file it as the
 * ninth thumbnail in the rail and undo the reason it is held back.
 *
 * Reading the schema's list also drops a dependency this used to carry quietly —
 * that the content records happen to author their takes in gallery order. They
 * do; it was never enforced anywhere, and `artSource` already answers null for a
 * take the car does not have, so asking the schema costs nothing and states the
 * order out loud.
 *
 * This is the full-screen viewer's list (SPEC-012 D-10). The gallery band's is
 * the same one minus the take the card above it is showing, which is why
 * `gallerySources` is a filter over this rather than a second walk of the record.
 */
export function artSources(car) {
    return GALLERY_TAKES
        .map((take) => artSource(car, take))
        .filter(Boolean);
}

/** Every take EXCEPT the one the card above the gallery is already showing. */
export function gallerySources(car, shownTake = HERO_TAKE) {
    return artSources(car).filter((source) => source.take !== shownTake);
}
